import * as React from 'react'

import { Box, Text } from '@factorialco/deprecated-design-system'
import { Features } from 'generated/featureDefinitions'

import { observer } from 'mobx-react'

import { track, trackingEvents } from 'modules/core/lib/Tracking'
import i18n from 'modules/core/lib/i18n'

import { useAttendanceFeatures } from 'modules/attendance/lib/attendanceFeaturesContext'
import { locationTypeText } from 'modules/attendance/lib/locationType'
import attendancePolicies from 'modules/attendance/store/collections/policies'
import attendanceSettingsCollection from 'modules/attendance/store/collections/settings'
import ShiftVersionHistory, {
  SHIFT_CHANGE_TYPES,
} from 'modules/attendance/store/models/ShiftVersionHistory'
import type ShiftVersionHistoryType from 'modules/attendance/store/models/ShiftVersionHistory'

interface DescriptionProps {
  history: ShiftVersionHistoryType
}

const manualDescription = (history: ShiftVersionHistoryType) => {
  const { attributes, authorName, changes, clockIn, clockOut, isForgotClockOut } = history
  const {
    old_clock_in,
    old_clock_out,
    old_location_type,
    new_clock_in,
    new_clock_out,
    new_observations,
    new_location_type,
  } = attributes

  const { creation, observation, location_type, clock_in_out } = SHIFT_CHANGE_TYPES

  const getHistoryChangeDescription = (change: string) => {
    switch (change) {
      case creation:
        if (!!clockIn && !!clockOut)
          return i18n.t('clock_in.history.descriptions.created', {
            clock_in: clockIn,
            clock_out: clockOut,
            author: authorName,
          })
        if (!clockIn && !clockOut)
          return i18n.t('clock_in.history.descriptions.forfait_jours_created', {
            author: authorName,
          })
      case observation:
        return i18n.t('clock_in.history.descriptions.observation_updated', {
          author: authorName,
          observation_text: new_observations,
        })
      case location_type:
        return i18n.t('clock_in.history.descriptions.location_type_updated', {
          author: authorName,
          old_location_type: locationTypeText(old_location_type),
          new_location_type: locationTypeText(new_location_type),
        })
      case clock_in_out:
        if (!old_clock_out)
          return i18n.t('clock_in.history.descriptions.clock_in_updated', {
            old_clock_in,
            new_clock_in,
            author: authorName,
          })
        else {
          if (!isForgotClockOut)
            return i18n.t('clock_in.history.descriptions.updated', {
              old_clock_in,
              old_clock_out,
              new_clock_in,
              new_clock_out,
              author: authorName,
            })
          else
            return i18n.t('clock_in.history.descriptions.updated_with_forgot_clock_out', {
              old_clock_out,
              new_clock_out,
              author: authorName,
            })
        }
    }
  }

  return (
    <Box>
      {changes.map((change, i) => (
        <Text key={i} size="200" color="grey200">
          {getHistoryChangeDescription(change)}
        </Text>
      ))}
    </Box>
  )
}

const historyDescription = (
  history: ShiftVersionHistoryType,
  hasGeofencingFeature: boolean,
  options: Record<string, React.ReactNode>
) => {
  if (history.clockedInWithLocationTypeAndLat) {
    return hasGeofencingFeature
      ? i18n.tView(
          'clock_in.history.descriptions.clocked_in_with_location_type_with_location',
          options
        )
      : i18n.tView('clock_in.history.descriptions.clocked_in_with_location_type_with_link', options)
  }

  if (history.clockedInWithLocationType) {
    return i18n.tView('clock_in.history.descriptions.clocked_in_with_location_type', options)
  }

  if (history.hasLatitudeAndLongitude) {
    if (history.typeClockedIn) {
      return hasGeofencingFeature
        ? i18n.tView('clock_in.history.descriptions.clocked_in_with_only_location', options)
        : i18n.tView('clock_in.history.descriptions.clocked_in', options)
    }

    if (history.typeClockedOut) {
      return hasGeofencingFeature
        ? i18n.tView('clock_in.history.descriptions.clocked_out_with_location', options)
        : i18n.tView('clock_in.history.descriptions.clocked_out', options)
    }
  }

  if (history.typeCreated) {
    return i18n.tView('clock_in.history.descriptions.created', options)
  }

  if (history.typeUpdated) {
    return i18n.tView('clock_in.history.descriptions.updated', options)
  }

  return null
}

const clockInOutDescription = (
  history: ShiftVersionHistory,
  hasGeofencingFeature: boolean,
  hasGeoLocationFeature: boolean
) => {
  if (!hasGeoLocationFeature || (!history.hasLatitudeAndLongitude && !history.hasLocationType))
    return

  const { latitude, longitude, locationType } = history
  const link = `https://www.google.com/maps/search/${latitude},${longitude}`

  const options = {
    link: (
      <a
        href={link}
        onClick={() => track(trackingEvents.timeTracking.navigateGeolocationMap)}
        rel="nofollow noopener noreferrer"
        target="_blank"
        style={{
          outline: 'none',
        }}
      >
        {i18n.t('clock_in.history.descriptions.address')}
      </a>
    ),

    author: (
      <Box inline component="span">
        {history.authorName}
      </Box>
    ),

    locationType: (
      <Box inline component="span">
        {locationTypeText(locationType)}
      </Box>
    ),
  }

  return historyDescription(history, hasGeofencingFeature, options)
}

const description = (
  history: ShiftVersionHistory,
  hasGeofencingFeature: boolean,
  hasGeoLocationFeature: boolean
) => {
  if (history.typeCreatedOrUpdated) {
    return manualDescription(history)
  }

  if (history.typeClockedInOrClockedOut) {
    return clockInOutDescription(history, hasGeofencingFeature, hasGeoLocationFeature)
  }
}

const Description = ({ history }: DescriptionProps) => {
  const { featureAvailability: hasGeofencingFeatureAvailability } = useAttendanceFeatures(
    Features.time_tracking_geofencing_alerts
  )

  const policy = attendancePolicies.get(0)
  const currentSetting = attendanceSettingsCollection.currentByPolicy(policy?.id)
  const hasGeofencingAlertsEnabled = !!currentSetting?.hasGeofencingEnabled
  const hasGeoLocationEnabled = !!currentSetting?.hasGeolocationEnabled

  const hasGeofencingFeature = hasGeofencingFeatureAvailability && hasGeofencingAlertsEnabled

  const text = description(history, hasGeofencingFeature, hasGeoLocationEnabled)
  if (!text) return null

  return (
    <Box paddingY="s2" style={{ textAlign: 'left' }}>
      <Text size="300" color="grey200">
        {text}
      </Text>
    </Box>
  )
}

export default observer(Description)
